import { getMapConfig } from '../domain/maps.js';
import { MinionSpawner } from '../domain/minionSpawner.js';
import { getAramBridgeWallBoxes } from '../domain/map/aramBridgeCollision.js';
import { ARAM_BRIDGE_WIDTH } from '../domain/map/aramScale.js';
import { createMapStructures } from './mapStructureFactory.js';

/**
 * buildMatchMapLoad — Pure match layout plan from mapId (P3-02).
 * Structures for live match come only via mapStructureFactory (P3-03).
 *
 * Contrato: buildMatchMapLoad(mapId) → MatchMapLoadPlan
 *           createTurretsFromPlan / createSpawnersFromPlan
 */

const ARAM_BRUSH_X = ARAM_BRIDGE_WIDTH / 2 - 2.5;

const SANDBOX_LAYOUTS = {
  test_map: {
    brushes: [
      { x: 6, z: -4, radius: 2.8 },
      { x: 5, z: 5, radius: 2.2 }
    ],
    wallBoxes: [{ minX: -8.5, maxX: -4.5, minZ: -1.0, maxZ: 2.5 }],
    impassableZones: [{ type: 'circle', x: -6.5, z: 0.8, radius: 2.8 }]
  },
  aram_proving_grounds: {
    brushes: [
      { id: 'aram-brush-w-mid', x: -ARAM_BRUSH_X, z: 0, radius: 2.6 },
      { id: 'aram-brush-e-mid', x: ARAM_BRUSH_X, z: 0, radius: 2.6 },
      { id: 'aram-brush-w-blue', x: -ARAM_BRUSH_X, z: 35, radius: 2.4 },
      { id: 'aram-brush-e-red', x: ARAM_BRUSH_X, z: -35, radius: 2.4 }
    ],
    wallBoxes: getAramBridgeWallBoxes(),
    impassableZones: []
  }
};

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function resolveLayout(mapId) {
  return SANDBOX_LAYOUTS[mapId] || {
    brushes: [],
    wallBoxes: [],
    impassableZones: []
  };
}

function buildSpawnerSpecs(lanes = []) {
  const lane = lanes.find((l) => l.id === 'mid') || lanes[0];
  if (!lane || !Array.isArray(lane.points) || lane.points.length < 2) {
    return [];
  }
  const points = lane.points;
  const start = points[0];
  const end = points[points.length - 1];
  const forward = points.slice(1);
  const reverse = [...points].reverse().slice(1);
  return [
    {
      team: 'blue',
      spawnX: start.x,
      spawnZ: start.z,
      waypoints: cloneJson(forward),
      sequentialDelay: 0.8
    },
    {
      team: 'red',
      spawnX: end.x,
      spawnZ: end.z,
      waypoints: cloneJson(reverse),
      sequentialDelay: 0.8
    }
  ];
}

export function buildMatchMapLoad(mapId) {
  const config = getMapConfig(mapId);
  const resolvedId = config.id;
  const layout = resolveLayout(resolvedId);

  return {
    mapId: resolvedId,
    coordSpace: 'sandbox_xz',
    bounds: cloneJson(config.bounds),
    brushes: cloneJson(layout.brushes),
    wallBoxes: cloneJson(layout.wallBoxes),
    impassableZones: cloneJson(layout.impassableZones),
    structureBlueprints: cloneJson(config.turrets || []),
    spawnerSpecs: buildSpawnerSpecs(config.lanes),
    jungleCamps: cloneJson(config.jungleCamps || []),
    objectives: {
      altars: cloneJson(config.altars || []),
      relics: cloneJson(config.relics || [])
    },
    passiveGoldRate: config.passiveGoldRate,
    passiveXpRate: config.passiveXpRate ?? 0
  };
}

export function createTurretsFromPlan(plan) {
  return createMapStructures(plan.mapId, {
    coordSpace: plan.coordSpace || 'sandbox_xz'
  });
}

export function createSpawnersFromPlan(plan, turrets = []) {
  const byTeam = Object.fromEntries(turrets.map((t) => [t.team, t]));
  return (plan.spawnerSpecs || []).map((spec) => {
    const enemyTeam = spec.team === 'blue' ? 'red' : 'blue';
    return new MinionSpawner({
      team: spec.team,
      spawnX: spec.spawnX,
      spawnZ: spec.spawnZ,
      waypoints: spec.waypoints,
      mainTurretTarget: byTeam[enemyTeam] || null,
      sequentialDelay: spec.sequentialDelay ?? 0.8
    });
  });
}
